/**
 * The mock backend.
 *
 * This module is the ONLY place that knows the data is fabricated and held in
 * memory. Services above it expose an async interface; components above those
 * see nothing but promises. Replacing this file with calls to the FastAPI
 * backend is the whole migration.
 *
 * Writes persist for the life of the JavaScript realm — the browser tab in the
 * client, the server process on the server. A hard reload reseeds. That is a
 * property of the mock, not of the interface.
 */
import { seedBorrowers, seedCases } from "@/mocks";
import type { Borrower, CaseRecord } from "@/lib/types";

interface Store {
  borrowers: Borrower[];
  cases: CaseRecord[];
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

let store: Store | null = null;

function db(): Store {
  store ??= { borrowers: clone(seedBorrowers), cases: clone(seedCases) };
  return store;
}

/** Reset to the seed. Tests call this; the application never does. */
export function resetStore(): void {
  store = null;
}

/** Simulates the latency of a real call so loading states are exercised. */
async function tick<T>(value: T): Promise<T> {
  return Promise.resolve(clone(value));
}

export async function readBorrowers(): Promise<Borrower[]> {
  return tick(db().borrowers);
}

export async function readCases(): Promise<CaseRecord[]> {
  return tick(db().cases);
}

export async function readCase(caseId: string): Promise<CaseRecord | null> {
  const record = db().cases.find((entry) => entry.case.id === caseId);
  return record ? tick(record) : null;
}

/**
 * Applies `mutate` to the live record and returns a copy of the result.
 * Returns null when the case does not exist, so callers can 404 rather than
 * silently succeed.
 */
export async function writeCase(
  caseId: string,
  mutate: (record: CaseRecord) => void,
): Promise<CaseRecord | null> {
  const record = db().cases.find((entry) => entry.case.id === caseId);
  if (!record) return null;
  mutate(record);
  return tick(record);
}
